import type { ReactNode } from 'react'
import { HelpIcon } from '../common/HelpIcon'
import s from './Card.module.scss'

/**
 * Wie viele der zwölf Spalten die Karte auf dem Desktop belegt.
 *
 * Absichtlich nur diese fünf Werte und keine freie Zahl: Zwölf teilt sich
 * durch 3, 4, 6 — Drittel, Viertel und Hälften gehen ohne Restspalte auf. Eine
 * Karte mit `span={5}` ließe eine 7er-Lücke, die keine zweite Karte füllt.
 */
export type CardSpan = 3 | 4 | 6 | 8 | 12

interface Props {
  title?: string
  /** Setzt den Titel als `// GROSSBUCHSTABEN` in Monospace, wie in POC v1. */
  label?: boolean
  /** Rechts in der Kopfzeile: Anzahl, Stand, Einheit. */
  meta?: ReactNode
  /** Kurzbeschreibung hinter dem Titel. */
  help?: ReactNode
  span?: CardSpan
  /** Ohne Innenabstand — für Tabellen, die bis an den Rand laufen. */
  flush?: boolean
  footer?: ReactNode
  children: ReactNode
}

export function Card({ title, label, meta, help, span = 12, flush,
                      footer, children }: Props) {
  return (
    <section className={`${s.card} ${flush ? '' : s.padded}`} data-span={span}>
      {title && (
        <div className={`${s.head} ${flush ? s.headFlush : s.headPadded}`}>
          <div className={s.titleRow}>
            {label ? <span className={s.label}>{title}</span> : <h2>{title}</h2>}
            {help && <HelpIcon term={title}>{help}</HelpIcon>}
          </div>
          {meta && <span className={s.meta}>{meta}</span>}
        </div>
      )}

      <div className={s.body}>{children}</div>

      {/*
        Die Fusszeile sitzt bei `flush` am Kartenrand und bei gepolsterter Karte
        muesste sie den Abstand ueberziehen — deshalb negative Aussenabstaende
        statt einer zweiten Regel im Modul.
      */}
      {footer && (
        <div className={s.foot} data-inset={!flush}>{footer}</div>
      )}
    </section>
  )
}
